import getMatchingVariant from '../utils/variants/getMatchingVariant';
import updatePrice from '../utils/variants/updatePrice';
import updateSelectedVariant from '../utils/variants/updateSelectedVariants';
import updateFormControls from '../utils/variants/updateFormControls';
import updateURL from '../utils/variants/updateURL';
import preselectVariantFromURL from '../utils/variants/preselectVariantFromURL';
import updateStockIndicator from '../utils/variants/updateStockIndicator';
import updateCartSubtotalPreview from '../utils/variants/updateCartSubtotalPreview';

const ATTR_MAX_QUANTITY = 'data-max-quantity';
import updateGalleryImage from '../utils/variants/updateGalleryImage';

const initProductVariants = (container = document.querySelector('[data-product-main]')) => {
  if (!container) return;
  const form = container.querySelector('[data-product-form]');
  if (!form) return;

  const variantEls = container.querySelectorAll('[data-variant-input]');
  const sellingPlanEls = container.querySelectorAll('[data-selling-plan-radio]');
  const hasLinePricingJson = Boolean(form.querySelector('[data-variant-line-pricing-json]')?.textContent?.trim());
  if (!variantEls.length && !sellingPlanEls.length && !hasLinePricingJson) return;

  const variantsDataEl = form.querySelector('[data-product-variants-json]');
  const optionNamesEl = form.querySelector('[data-product-options-json]');
  const variantInventoryEl = form.querySelector('[data-product-variants-inventory-policy-json]');
  const variantIdInput = form.querySelector('[data-atc-id-input]');
  const sellingPlanGroupContainerEl = form.querySelector('[data-selling-plan-groups]');
  const sellingPlanIdInput = sellingPlanGroupContainerEl?.querySelector('[data-selling-plan-input]');
  const quantitySelector = form.querySelector('[data-quantity-selector]');
  const quantityInput = quantitySelector?.querySelector('[data-quantity-input]');
  const atcButton = form.querySelector('button[type="submit"]');
  const atcButtonPrice = atcButton?.querySelector('[data-atc-price]');
  const atcButtonMsg = atcButton?.querySelector('[data-atc-price-msg]');
  const productControls = container.querySelector('[data-product-controls]');
  const optionValueElement = form.querySelector('[data-option-value]');
  const stockIndicator = container.querySelector('[data-stock-indicator]');

  const activeVariantAttribute = 'data-active-variant';

  let variantsData = [];
  let optionNames = [];
  let variantInventory = [];

  let variantAdditionalProductMap = {};
  let variantLinePricingMap = {};
  try {
    variantsData = JSON.parse(variantsDataEl?.textContent || '[]');
    optionNames = JSON.parse(optionNamesEl?.textContent || '[]');
    variantInventory = JSON.parse(variantInventoryEl?.textContent || '[]');
  } catch (e) {
    console.error('Invalid variant/options JSON', e);
    return;
  }
  try {
    const addonEl = form.querySelector('[data-variant-additional-product-json]');
    if (addonEl?.textContent) {
      variantAdditionalProductMap = JSON.parse(addonEl.textContent);
    }
  } catch (e) {
    console.error('Invalid variant additional-product JSON', e);
  }
  try {
    const lineEl = form.querySelector('[data-variant-line-pricing-json]');
    if (lineEl?.textContent) {
      variantLinePricingMap = JSON.parse(lineEl.textContent);
    }
  } catch (e) {
    console.error('Invalid variant line pricing JSON', e);
  }

  const selectedOptions = {};

  const priceContainer = document.querySelector('[data-product-price-pdp]');
  const priceEl = priceContainer?.querySelector('[data-product-price]');
  const compareAtPriceEl = priceContainer?.querySelector('[data-product-compare-at-price]');
  const badgeEl = priceContainer?.querySelector('[data-product-sale-badge]');
  const priceFromEl = priceContainer?.querySelector('[data-product-price-from]');

  const subtotalRow = form.querySelector('[data-product-cart-subtotal-row]');
  const subtotalLabelEl = form.querySelector('[data-product-cart-subtotal-label]');
  const subtotalMainEl = form.querySelector('[data-product-cart-subtotal-main]');
  const subtotalPlusEl = form.querySelector('[data-product-cart-subtotal-plus]');
  const subtotalAddonEl = form.querySelector('[data-product-cart-subtotal-addon]');
  const subtotalEqualsEl = form.querySelector('[data-product-cart-subtotal-equals]');
  const subtotalTotalEl = form.querySelector('[data-product-cart-subtotal-total]');

  const moneyFormat = window.theme?.moneyFormat;

  const updateVariantDetails = (variantStock, shouldUpdateImage = false) => {
    const variant = getMatchingVariant(selectedOptions, variantsData, optionNames);
    if (!variant) return;

    const resolvedQty =
      typeof variant.inventory_quantity === 'number' && !Number.isNaN(variant.inventory_quantity)
        ? variant.inventory_quantity
        : Number(variantStock);

    variantIdInput && (variantIdInput.value = variant.id);

    if (stockIndicator) {
      updateStockIndicator(stockIndicator, {
        quantity: resolvedQty,
        available: variant.available,
      });
    }
    updatePrice({
      priceEl,
      compareAtPriceEl,
      badgeEl,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      badgeTemplate: window.theme.strings.priceBadgeText,
      moneyFormat
    });

    if (priceFromEl) {
      const hasAddon = Boolean(variantAdditionalProductMap[String(variant.id)]);
      priceFromEl.classList.toggle('hidden', !hasAddon);
    }

    if (subtotalRow && Object.keys(variantLinePricingMap).length) {
      updateCartSubtotalPreview({
        pricingMap: variantLinePricingMap,
        variantId: variant.id,
        labelEl: subtotalLabelEl,
        mainEl: subtotalMainEl,
        plusEl: subtotalPlusEl,
        addonEl: subtotalAddonEl,
        equalsEl: subtotalEqualsEl,
        totalEl: subtotalTotalEl
      });
    }

    updateSelectedVariant({
      variant,
      variantInventory,
      elements: {
        productControls,
        atcButtonMsg,
        optionValueElement
      },
      strings: window.theme.strings
    });

    updateURL({ variantID: variant.id, sellingPlanIdInput });

    if(shouldUpdateImage) {
      updateGalleryImage(variant);
    }

    updateFormControls({
      variant,
      qty: +quantityInput.value,
      variantsData,
      optionNames,
      selectedOptions,
      atcButton,
      atcButtonPrice,
      quantitySelector
    });

    if (quantitySelector) {
      quantitySelector.dataset.maxQuantity = String(Number.isFinite(resolvedQty) ? resolvedQty : 0);
    }
  };

  const updateSelectedOption = (name, value, variantStock, shouldUpdateImage = false) => {
    selectedOptions[name] = value;
    updateVariantDetails(variantStock, shouldUpdateImage);
  };

  if (variantEls.length) {
    variantEls.forEach(variant => {
      variant.addEventListener('click', ({ target }) => {
        if(target.hasAttribute(activeVariantAttribute)) {
          const variant = getMatchingVariant(selectedOptions, variantsData, optionNames);
          updateGalleryImage(variant);
        }
      });

      variant.addEventListener('change', ({ target }) => {
        target.setAttribute(activeVariantAttribute, '');
        variantEls.forEach(variant => {
          if (variant !== target) {
            variant.removeAttribute(activeVariantAttribute);
          }
        });
        const variantStock = target.getAttribute('data-max-quantity');
        const { name, value } = target;
        const shouldUpdateImage = true;
        updateSelectedOption(name, value, variantStock, shouldUpdateImage);
      });
    });
  }

  preselectVariantFromURL({
    variantsData,
    optionNames,
    form,
    selectedOptions,
    sellingPlanIdInput,
    updateVariantDetails
  });

  if (variantEls.length) {
    variantEls.forEach((el) => {
      if (el.checked) selectedOptions[el.name] = el.value;
    });
    const activeEl =
      form.querySelector('[data-variant-input][data-active-variant]') ||
      form.querySelector('[data-variant-input]:checked');
    if (activeEl && getMatchingVariant(selectedOptions, variantsData, optionNames)) {
      updateVariantDetails(activeEl.getAttribute(ATTR_MAX_QUANTITY));
    }
  } else if (hasLinePricingJson && variantsData.length === 1) {
    updateVariantDetails(quantitySelector?.getAttribute(ATTR_MAX_QUANTITY));
  }
};

export default initProductVariants;
